import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLines, FadeUp, Eyebrow } from "../components/Reveal";
import { IMG } from "./HomePage";

const BOILER_PRICES = [
  { label: "Fino a 300 litri", price: "CHF 390" },
  { label: "Da 301 a 500 litri", price: "CHF 590" },
  { label: "Da 501 a 800 litri", price: "CHF 790" },
  { label: "Oltre 800 litri", price: "CHF 1.10 per litro · minimo CHF 890" },
];

const PriceTable = ({ rows, testid }) => (
  <div className="border border-ink/10 bg-white" data-testid={testid}>
    {rows.map((r, i) => (
      <div key={r.label} className={`flex items-baseline justify-between gap-6 px-6 py-4 ${i > 0 ? "border-t border-ink/5" : ""}`}>
        <span className="text-sm text-ink/70">{r.label}</span>
        <span className="font-display font-medium text-lg text-antracite text-right">{r.price}</span>
      </div>
    ))}
  </div>
);

const NoteList = ({ items, icon: Icon, testid }) => (
  <ul className="space-y-3" data-testid={testid}>
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3 text-sm text-ink/70">
        <Icon className="w-4 h-4 text-bronzo mt-0.5 shrink-0" />
        {item}
      </li>
    ))}
  </ul>
);

const ServiceSection = ({ id, index, title, img, imgAlt, children, priceBlock, notes, cta, invert, contain }) => (
  <section id={id} className={`py-16 lg:py-24 ${index % 2 === 1 ? "bg-beige" : ""}`} data-testid={`service-section-${id}`}>
    <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <FadeUp className={`lg:col-span-5 ${invert ? "lg:order-2" : ""}`}>
          <div className={`img-frame aspect-[4/3] lg:aspect-[4/5] lg:max-h-[560px] w-full ${contain ? "bg-white border border-ink/10" : ""}`}>
            <img src={img} alt={imgAlt} className={`w-full h-full ${contain ? "object-contain p-8 lg:p-12" : "object-cover"}`} loading="lazy" />
          </div>
        </FadeUp>
        <div className={`lg:col-span-7 ${invert ? "lg:order-1" : ""}`}>
          <FadeUp>
            <Eyebrow>Servizio 0{index + 1}</Eyebrow>
            <h2 className="mt-5 font-display font-normal text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink text-balance">
              {title}
            </h2>
            <div className="mt-6 text-ink/70 leading-relaxed space-y-4">{children}</div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-8">{priceBlock}</div>
            <div className="mt-6 space-y-5">{notes}</div>
            <Link
              to={`/contatti?servizio=${id}`}
              data-testid={`servizi-cta-${id}`}
              className="mt-8 inline-flex items-center gap-2 bg-antracite text-avorio font-medium px-8 py-4 hover:bg-bronzo transition-colors duration-300"
            >
              {cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
);

export default function ServiziPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [hash]);

  return (
    <div data-testid="servizi-page">
      <Seo
        title="Servizi: pulizia boiler, lavaggio serpentine, filtri acqua | V32 Group Ticino"
        description="Pulizia e decalcificazione boiler da CHF 390, lavaggio circuiti di riscaldamento a pavimento a CHF 80 per circuito, manutenzione filtri acqua sanitaria a CHF 150. Interventi in Ticino con rapporto documentato."
      />

      <section className="pt-32 md:pt-44 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>V32 Manutenzioni Tecniche</Eyebrow>
          <MaskedLines
            lines={["Tre servizi specializzati.", "Un unico referente."]}
            delay={0.15}
            className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-ink max-w-3xl"
          />
          <FadeUp delay={0.5}>
            <p className="mt-8 text-ink/70 text-base md:text-lg leading-relaxed max-w-2xl">
              Manutenzione ordinaria di boiler, circuiti di riscaldamento a pavimento
              e filtri principali dell'acqua sanitaria. Prezzi finali per gli
              interventi standard, confermati prima dell'esecuzione, e rapporto
              documentato a fine lavoro.
            </p>
            <nav className="mt-8 flex flex-wrap gap-3" aria-label="Vai al servizio">
              {[
                ["#boiler", "Pulizia boiler", "service-nav-boiler"],
                ["#serpentine", "Lavaggio serpentine", "service-nav-serpentine"],
                ["#filtri", "Filtri acqua", "service-nav-filtri"],
              ].map(([href, label, tid]) => (
                <a
                  key={href}
                  href={href}
                  data-testid={tid}
                  className="text-sm font-medium border border-ink/15 px-5 py-2.5 text-ink/70 hover:border-bronzo hover:text-bronzo transition-colors duration-300"
                >
                  {label}
                </a>
              ))}
            </nav>
          </FadeUp>
        </div>
      </section>

      <ServiceSection
        id="boiler"
        index={0}
        title="Pulizia e decalcificazione boiler"
        img={IMG.boiler}
        imgAlt="Boiler per acqua calda sanitaria in un locale tecnico"
        cta="Prenota pulizia boiler"
        priceBlock={<PriceTable rows={BOILER_PRICES} testid="boiler-prices" />}
        notes={[
          <NoteList
            key="inc"
            testid="boiler-included"
            icon={Check}
            items={[
              "Il prezzo comprende la manutenzione ordinaria prevista per un impianto accessibile e in condizioni standard.",
              "La periodicità della manutenzione viene determinata in funzione dell'impianto, della qualità dell'acqua e delle indicazioni tecniche pertinenti.",
            ]}
          />,
          <NoteList
            key="exc"
            testid="boiler-excluded"
            icon={X}
            items={["Ricambi straordinari, riparazioni e lavorazioni aggiuntive non sono inclusi e vengono comunicati prima dell'esecuzione."]}
          />,
        ]}
      >
        <p>
          La manutenzione periodica del boiler permette di rimuovere i depositi di
          calcare, controllare lo stato dei componenti accessibili e individuare
          eventuali anomalie. V32 esegue interventi programmati con rapporto finale
          documentato.
        </p>
      </ServiceSection>

      <ServiceSection
        id="serpentine"
        index={1}
        invert
        title="Lavaggio circuiti di riscaldamento a pavimento"
        img={IMG.manifold}
        imgAlt="Collettore di un impianto di riscaldamento a pavimento con circuiti e valvole"
        cta="Richiedi lavaggio serpentine"
        priceBlock={
          <PriceTable
            testid="serpentine-prices"
            rows={[
              { label: "Per circuito", price: "CHF 80" },
              { label: "Minimo d'intervento", price: "CHF 390" },
              { label: "Esempio: 10 circuiti", price: "CHF 800" },
            ]}
          />
        }
        notes={[
          <NoteList
            key="inc"
            testid="serpentine-included"
            icon={Check}
            items={["Il servizio ordinario riguarda impianti accessibili e tecnicamente idonei al lavaggio previsto."]}
          />,
          <NoteList
            key="exc"
            testid="serpentine-excluded"
            icon={X}
            items={[
              "Riparazioni e sostituzione di componenti.",
              "Trattamenti chimici straordinari e modifiche idrauliche.",
              "Interventi su impianti non funzionanti o non accessibili.",
            ]}
          />,
        ]}
      >
        <p>
          Interventi di lavaggio e manutenzione dei circuiti di riscaldamento a
          pavimento, con verifica delle condizioni operative e documentazione del
          lavoro eseguito.
        </p>
        <p className="text-sm text-ink/50">
          Il lavaggio è una manutenzione dell'impianto: la risoluzione di problemi
          specifici di riscaldamento o circolazione viene valutata caso per caso.
        </p>
      </ServiceSection>

      <ServiceSection
        id="filtri"
        index={2}
        title="Manutenzione filtri acqua sanitaria"
        img={IMG.filter}
        contain
        imgAlt="Filtro principale GF JRG per acqua sanitaria con cartuccia sostituibile"
        cta="Prenota manutenzione filtro"
        priceBlock={
          <PriceTable
            testid="filtri-prices"
            rows={[{ label: "Filtro principale standard — cartuccia compresa", price: "CHF 150" }]}
          />
        }
        notes={[
          <NoteList
            key="inc"
            testid="filtri-included"
            icon={Check}
            items={[
              "Il prezzo si applica a un filtro principale standard accessibile.",
              "Comprende una cartuccia filtrante standard compatibile, dove prevista.",
            ]}
          />,
          <NoteList
            key="exc"
            testid="filtri-excluded"
            icon={X}
            items={[
              "Non comprende corpi filtro completi, ricambi speciali o riparazioni.",
              "I sistemi autopulenti e i filtri particolari vengono valutati in base al modello.",
              "Per impianti condominiali complessi viene preparato un preventivo.",
            ]}
          />,
        ]}
      >
        <p>
          Manutenzione del filtro principale installato all'ingresso dell'impianto
          dell'acqua sanitaria, con sostituzione dell'elemento filtrante standard
          compatibile, verifica del corretto rimontaggio e controllo finale.
        </p>
        <p className="text-sm text-ink/50">
          Prima di confermare l'intervento ti chiederemo, se possibile, una
          fotografia del filtro con marca e modello: ci permette di verificare la
          disponibilità e la compatibilità del ricambio.
        </p>
      </ServiceSection>

      <section className="bg-antracite text-avorio py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <FadeUp>
            <h2 className="font-display font-light text-2xl sm:text-3xl lg:text-4xl tracking-tight text-balance max-w-xl">
              Non sai quale intervento serve al tuo impianto?
            </h2>
            <p className="mt-4 text-avorio/70 max-w-lg">
              Descrivici la situazione: ti indichiamo il servizio corretto e il
              relativo prezzo prima di qualsiasi impegno.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link
              to="/contatti"
              data-testid="servizi-cta-generico"
              className="inline-flex items-center gap-2 bg-bronzo text-white font-medium px-8 py-4 hover:bg-avorio hover:text-antracite transition-colors duration-300 shrink-0"
            >
              Richiedi un intervento <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
